import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Chip,
  OutlinedInput,
  FormControl,
  InputLabel,
} from "@mui/material";
import api from "../../../API/axios";
import NotificationSnackbar from "../../../components/NotificationSnackbar";
import useNotification from "../../../hooks/useNotification";
import { useAuthApi } from "../../../hooks/useAuthApi";
import { countries } from "../../../utils/country";
import { useRoleNavigator } from "../../../utils/roleNavigator";
import { handleFieldChange } from "../../../utils/handleFieldChange";
import { sanitizeInput } from "../../../utils/inputSanitizer";

function TabPanel({ children, value, index }) {
  return (
    <div style={{ display: value === index ? "block" : "none", width: "100%" }}>
      <Box sx={{ p: 2 }}>{children}</Box>
    </div>
  );
}

export default function EditOperatorTabs() {
  const { sendResetPasswordRequest } = useAuthApi();
  const navigateByRole = useRoleNavigator();
  const { id } = useParams();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const { notification, showNotification, hideNotification } =
    useNotification();

  if (isRegistered) {
    console.log("Operator is registered");
  }
  // Details tab state
  const [details, setDetails] = useState({
    name: "",
    email: "",
    telephone: "",
    address: "",
    address2: "",
    country: "GB",
    state: "",
    city: "",
    postCode: "",
    enabled: true,
    twoFactorMethod: "",
    uniqueId: "",
    payoutAmount: "",
  });
  // Removed isRegistered state, backend handles update restrictions

  const [phoneCode, setPhoneCode] = useState(
    countries.find((c) => c.code === details.country)?.phoneCode || "+44"
  );

  useEffect(() => {
    const selected = countries.find((c) => c.code === details.country);
    setPhoneCode(selected ? selected.phoneCode : "+44");
  }, [details.country]);

  // QR Code state
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrCodeLoading, setQrCodeLoading] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    async function fetchOperatorAndClients() {
      setLoading(true);
      try {
        // Fetch operator details
        const operatorRes = await api.get(`/operators/${id}`);
        const operatorData =
          operatorRes.data && operatorRes.data.data
            ? operatorRes.data.data
            : operatorRes.data;

        // --- Phone code extraction logic ---
        let telephone = operatorData.Telephone || operatorData.telephone || "";
        let countryCode = operatorData.Country || operatorData.country || "GB";
        let phoneCode =
          countries.find((c) => c.code === countryCode)?.phoneCode || "+44";
        // Remove phone code from telephone if present
        if (telephone.startsWith(phoneCode)) {
          telephone = telephone.replace(phoneCode, "").trim();
        }

        setOperator(operatorData);
        setDetails({
          name: operatorData.name || "",
          email: operatorData.email || "",
          telephone: telephone,
          address: operatorData.Address || operatorData.address || "",
          address2: operatorData.Address2 || operatorData.address2 || "",
          country: operatorData.Country || operatorData.country || "GB",
          state: operatorData.CountryState || operatorData.countryState || "",
          city: operatorData.TownCity || operatorData.townCity || "",
          postCode: operatorData.PostCode || operatorData.postCode || "",
          enabled: operatorData.status === 1 || operatorData.status === true,
          twoFactorMethod: operatorData.twoFactorMethod || "",
          uniqueId: operatorData.uniqueId || "",
          payoutAmount: operatorData.payoutAmount && operatorData.payoutAmount !== 0 ? operatorData.payoutAmount : "",
        });
        // Registration check: must be verified and have twoFactorMethod set (not 'none')
        setIsRegistered(
          (operatorData.isVerified === true ||
            operatorData.IsVerified === true) &&
            operatorData.twoFactorMethod &&
            operatorData.twoFactorMethod !== "none"
        );
      } catch (err) {
        console.error("Error fetching operator:", err);
        showNotification("Failed to load operator details", "error");
      }
      setLoading(false);
    }

    if (id) {
      fetchOperatorAndClients();
    }
  }, [id]);

  const handleDetailsChange = (field, value) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateDetails = () => {
    const newErrors = {};
    if (!details.name.trim()) newErrors.name = "Name is required";
    if (!details.email.trim()) newErrors.email = "Email is required";
    return newErrors;
  };

  const handleSubmitDetails = async () => {
    const newErrors = validateDetails();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showNotification("Please fix the validation errors", "error");
      return;
    }

    // Prefix phone code
    const selectedCountry = countries.find((c) => c.code === details.country);
    const phoneCode = selectedCountry ? selectedCountry.phoneCode : "+44";
    const telephoneWithCode = details.telephone.startsWith(phoneCode)
      ? details.telephone
      : `${phoneCode}${details.telephone.trim()}`;

    try {
      const payload = {
        name: details.name,
        email: details.email,
        telephone: telephoneWithCode,
        address: details.address,
        address2: details.address2,
        country: details.country,
        countryState: details.state,
        townCity: details.city,
        postCode: details.postCode,
        status: details.enabled,
        twoFactorMethod: details.twoFactorMethod,
        payoutAmount: details.payoutAmount ? parseFloat(details.payoutAmount) : "",
        role: "Operator",
      };

      await api.put(`/operators/${id}`, payload);
      showNotification("Operator details updated successfully", "success");
    } catch (err) {
      console.error("Error updating operator details:", err);
      showNotification("Failed to update operator details", "error");
    }
  };

  const generateOperatorQRCode = async () => {
    if (details.twoFactorMethod !== "app") return;

    setQrCodeLoading(true);
    try {
      const response = await api.post(`/operators/${id}/generate-qr`);
      const qrUrl =
        response.data?.data?.qrCodeUrl || response.data?.qrCodeUrl || "";
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error("Failed to generate QR code:", err);
      showNotification("Failed to generate QR code", "error");
    }
    setQrCodeLoading(false);
  };

  // Generate QR code when twoFactorMethod changes to 'app'
  useEffect(() => {
    if (details.twoFactorMethod === "app" && operator) {
      generateOperatorQRCode();
    }
  }, [details.twoFactorMethod, operator]);

  const handleResetPassword = async () => {
    const result = await sendResetPasswordRequest(id);
    if (result) {
      showNotification("Password reset email sent successfully", "success");
    } else {
      showNotification("Failed to send password reset email", "error");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography>Loading operator details...</Typography>
      </div>
    );
  }

  if (!operator) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography color="error">Operator not found</Typography>
        <Button
          //onClick={() => navigate("/superadmin/operators")}
          onClick={() => navigateByRole("operators")}
          sx={{ mt: 2 }}
        >
          Back to Operators
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ color: "var(--color-primary)", fontWeight: 700 }}
          >
            EDIT OPERATOR
          </Typography>
        </div>
        <Button
          variant="outlined"
          // onClick={() => navigate("/superadmin/operators")}
          onClick={() => navigateByRole("operators")}
          sx={{
            color: "#fff",
            borderColor: "#635f5fff",
            backgroundColor: "#635f5fff",
            "&:hover": { backgroundColor: "#333", borderColor: "#333" },
          }}
        >
          Back to Operators
        </Button>
      </div>

      {/* Tabs */}
      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              color: "#666",
              "&.Mui-selected": {
                color: "var(--color-primary)",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "var(--color-primary)",
            },
          }}
        >
          <Tab label="Details" />
          <Tab label="Payout" />
        </Tabs>

        {/* Details Tab */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
            >
              Operator Details
            </Typography>

            <Box sx={{ display: "flex", gap: 4 }}>
              {/* Left Column - Main Details */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flex: 1,
                  maxWidth: 600,
                }}
              >
                {/* Basic Information */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                >
                  Basic Information
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Name"
                    name="name"
                    autoFocus = {true}
                    value={details.name}
                    inputProps={{ maxLength: 30 }}
                    // onChange={(e) =>  handleDetailsChange("name", e.target.value)}
                    onChange={(e) =>
                      handleFieldChange(
                        "name",
                        sanitizeInput(e.target.value),
                        setDetails,
                        setErrors
                      )
                    }
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    size="medium"
                    sx={{ width: 270, borderRadius: 2, background: "#fff" }}
                  />

                  <TextField
                    label="Email"
                    type="email"
                    name="email"
                    value={details.email}
                    inputProps={{ maxLength: 50 }}
                    // onChange={(e) =>handleDetailsChange("email", e.target.value)}
                    onChange={(e) =>
                      handleFieldChange(
                        "email",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    size="medium"
                    sx={{ width: 270, borderRadius: 2, background: "#fff" }}
                  />
                </Box>

                <TextField
                  label="Telephone"
                  name="telephone"
                  required
                  value={details.telephone}
                  onChange={(e) =>
                    handleDetailsChange(
                      "telephone",
                      e.target.value.replace(/[^0-9]/g, "")
                    )
                  }
                  sx={{ width: 556, borderRadius: 2, background: "#fff" }}
                  inputProps={{ maxLength: 15 }}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          minWidth: 72,
                          mr: 1,
                          borderRight: "2px solid #E9E9E9",
                          pr: 1,
                        }}
                      >
                        {phoneCode}
                      </Box>
                    ),
                  }}
                />

                {/* Address Information */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#333", mb: 1, mt: 2 }}
                >
                  Address Information
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Address"
                    name="address"
                    required
                    value={details.address}
                    inputProps={{ maxLength: 30 }}
                    // onChange={(e) =>handleDetailsChange("address", e.target.value)}
                    onChange={(e) =>
                      handleFieldChange(
                        "address",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    error={!!errors.address}
                    helperText={errors.address}
                    size="medium"
                    sx={{ width: 270, borderRadius: 2, background: "#fff" }}
                  />
                  <TextField
                    label="Address 2"
                    name="address2"
                    value={details.address2}
                    inputProps={{ maxLength: 30 }}
                    onChange={(e) =>
                      handleDetailsChange("address2", e.target.value)
                    }
                    size="medium"
                    sx={{ width: 270, borderRadius: 2, background: "#fff" }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="County/State"
                    name="state"
                    required
                    inputProps={{ maxLength: 30 }}
                    value={details.state}
                    // onChange={(e) =>handleDetailsChange("state", e.target.value)}
                    onChange={(e) =>
                      handleFieldChange(
                        "state",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    error={!!errors.state}
                    helperText={errors.state}
                    size="medium"
                    sx={{ width: 270, borderRadius: 2, background: "#fff" }}
                  />

                  <TextField
                    label="Town/City"
                    name="city"
                    required
                    value={details.city}
                    // onChange={(e) =>handleDetailsChange("city", e.target.value)}
                    onChange={(e) =>
                      handleFieldChange(
                        "city",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    error={!!errors.city}
                    helperText={errors.city}
                    inputProps={{ maxLength: 30 }}
                    size="medium"
                    sx={{ width: 270, borderRadius: 2, background: "#fff" }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="PostCode/ZipCode"
                    name="postCode"
                    required
                    value={details.postCode}
                    // onChange={(e) =>handleDetailsChange("postCode", e.target.value)}
                    onChange={(e) => {
                      handleFieldChange(
                        "postCode",
                        e.target.value,
                        setDetails,
                        setErrors
                      );
                    }}
                    inputProps={{ maxLength: 10 }}
                    error={!!errors.postCode}
                    helperText={errors.postCode}
                    size="medium"
                    sx={{ width: 270, borderRadius: 2, background: "#fff" }}
                  />
                  <Box sx={{ width: 270 }}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderRadius: 2,
                        background: "#fff",
                        width: "100%",
                      }}
                    >
                      <InputLabel
                        htmlFor="operator-country-select"
                        sx={{
                          transform: "translate(14px, 18px) scale(0.9)",
                          "&.MuiInputLabel-shrink": {
                            transform: "translate(14px, 0px) scale(0.75)",
                          },
                        }}
                      >
                        Country
                      </InputLabel>
                      <Select
                        labelId="operator-country-label"
                        id="operator-country-select"
                        label="Country"
                        name="country"
                        value={details.country}
                        onChange={(e) =>
                          handleDetailsChange("country", e.target.value)
                        }
                        input={<OutlinedInput label="Country" />}
                        size="medium"
                        sx={{
                          background: "#fff",
                          width: "100%",
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 220,
                              overflowY: "auto",
                            },
                          },
                        }}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              {/* Right Column - Account Settings */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 300,
                  maxWidth: 350,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                >
                  Account Settings
                </Typography>

                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {/* Show toggle only if 2FA method is set */}
                  {details.twoFactorMethod &&
                    details.twoFactorMethod !== "none" && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={details.enabled}
                              onChange={(e) =>
                                handleDetailsChange("enabled", e.target.checked)
                              }
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#43a047",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor: "#43a047",
                                  },
                              }}
                            />
                          }
                          label="Operator Enabled"
                          sx={{ mb: 2 }}
                        />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mb: 3, fontSize: "0.875rem" }}
                        >
                          Disabling an operator will log them out and prevent
                          them from logging in until re-enabled.
                        </Typography>
                      </>
                    )}

                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                  >
                    Two-Factor Authentication
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Method:
                    </Typography>
                    <Chip
                      label={
                        !details.twoFactorMethod ||
                        details.twoFactorMethod === "none"
                          ? "Pending"
                          : details.twoFactorMethod === "email"
                          ? "Email"
                          : "Authenticator App"
                      }
                      color={
                        !details.twoFactorMethod ||
                        details.twoFactorMethod === "none"
                          ? "warning"
                          : details.twoFactorMethod === "email"
                          ? "error"
                          : "secondary"
                      }
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>

                  {/* QR Code Display for Authenticator App */}
                  {details.twoFactorMethod === "app" && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                      >
                        Authenticator App QR Code
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f9f9f9",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          textAlign: "center",
                        }}
                      >
                        {qrCodeLoading ? (
                          <Typography variant="body2" color="textSecondary">
                            Generating QR Code...
                          </Typography>
                        ) : qrCodeUrl ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, fontWeight: 500 }}
                            >
                              Scan with authenticator app:
                            </Typography>
                            <img
                              src={qrCodeUrl}
                              alt="QR Code for 2FA setup"
                              style={{
                                maxWidth: "180px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "white",
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ display: "block", mt: 1 }}
                            >
                              Use Google Authenticator, Microsoft Authenticator,
                              or similar apps
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{ mb: 1 }}
                            >
                              Failed to generate QR code
                            </Typography>
                            <Button
                              size="small"
                              onClick={generateOperatorQRCode}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Retry
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Paper>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    minWidth: 300,
                    maxWidth: 350,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmitDetails}
                    disabled={
                      !details.twoFactorMethod ||
                      details.twoFactorMethod === "none" ||
                      Object.values(errors).some((msg) => msg)
                    }
                    sx={{
                      backgroundColor: "var(--color-primary)",
                      "&:hover": { backgroundColor: "var(--color-secondary)" },
                    }}
                  >
                    Update Details
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetPassword}
                    disabled={
                      !details.twoFactorMethod ||
                      details.twoFactorMethod === "none"
                    }
                    sx={{
                      color: "#fff !important",
                      borderColor: "#666",
                      backgroundColor: "#635f5fff",
                      "&:hover": {
                        backgroundColor: "#635f5fff",
                      },
                    }}
                  >
                    Reset Password
                  </Button>
                  {(!details.twoFactorMethod ||
                    details.twoFactorMethod === "none") && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      Operator must complete two-factor registration before
                      performing account actions.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Payout Tab */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
            >
              Payout Settings
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Set the payout amount for this operator. This amount will be
              associated with each outbound message sent.
            </Typography>

            <TextField
              label="Payout Amount per Message"
              type="number"
              value={details.payoutAmount}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow 2 decimal places
                if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                  handleDetailsChange("payoutAmount", value);
                }
              }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
              }}
              size="medium"
              sx={{
                mt: 2,
                mb: 2,
                width: 300,
                borderRadius: 2,
                background: "#fff",
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              This payout amount will be used for operator pay reports and will
              be captured at the time each message is sent to ensure accuracy
              even if the rate changes later.
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmitDetails}
                color="error"
                sx={{
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-secondary)" },
                }}
              >
                Update Payout Settings
              </Button>
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
